import { Box, FormControlLabel, Grid, Switch, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguage } from '../../../../../context/LanguageContext';
import { RIGHTS, full, useRights } from '../../../../../rights';
import { todayDate } from '../dateUtils';
import type { OfferFormData } from './offerForm';
import { offerErrors } from '../validation/fieldErrors';

const OFFERS_KEY = 'pages.marketing.communication.offers';

interface OfferGeneralFieldsProps {
  offer: OfferFormData;
  onChange: (patch: Partial<OfferFormData>) => void;
  isRecurring: boolean;
  // Le nombre maximum d'éditions n'a de sens que pour un coupon caisse
  showMaxEdit: boolean;
}

function OfferGeneralFields({ offer, onChange, isRecurring, showMaxEdit }: OfferGeneralFieldsProps) {
  const { t } = useLanguage();
  const { can } = useRights();
  const canForceOfferNumber = can(full(RIGHTS.parameter.forceOfferNumber));
  const errors = offerErrors(offer, { isRecurring, isCashdesk: showMaxEdit });
  const fieldError = (name: keyof typeof errors) => ({ error: Boolean(errors[name]), helperText: errors[name] });
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth size="small" label="Titre de l'offre"
          value={offer.title}
          onChange={e => onChange({ title: e.target.value })}
          {...fieldError('title')}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth size="small" label="Description de l'offre"
          multiline rows={4}
          value={offer.description}
          onChange={e => onChange({ description: e.target.value })}
          {...fieldError('description')}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid size={{ xs: 8.4 }}>
            <TextField
              fullWidth size="small" label="Code offre"
              value={offer.offerNumber}
              onChange={e => onChange({ offerNumber: e.target.value })}
              // BeOne : saisir son propre code offre demande da.parameter.force_offer_number à 0 (sinon généré)
              disabled={!canForceOfferNumber}
              {...fieldError('offerNumber')}
            />
          </Grid>
          <Grid size={{ xs: 3.6 }}>
            <TextField
              fullWidth size="small" type="number" label="Nombre maximum d'utilisations"
              value={offer.nbMaxUse}
              onChange={e => onChange({ nbMaxUse: e.target.value })}
              {...fieldError('nbMaxUse')}
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Dates de validité
        </Typography>
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date et heure de début"
              InputLabelProps={{ shrink: true }}
              value={offer.startValidityDate}
              onFocus={() => { if (!offer.startValidityDate) onChange({ startValidityDate: `${todayDate()}T00:00` }); }}
              onChange={e => onChange({ startValidityDate: e.target.value })}
              {...fieldError('startValidityDate')}
              disabled={isRecurring}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date et heure de fin"
              InputLabelProps={{ shrink: true }}
              value={offer.endValidityDate}
              onFocus={() => {
                if (!offer.endValidityDate) onChange({ endValidityDate: `${(offer.startValidityDate || todayDate()).slice(0, 10)}T23:59` });
              }}
              onChange={e => onChange({ endValidityDate: e.target.value })}
              {...fieldError('endValidityDate')}
              disabled={isRecurring}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <FormControlLabel
            control={<Switch size="small" checked={offer.cumulable} onChange={e => onChange({ cumulable: e.target.checked })} />}
            label={t(`${OFFERS_KEY}.cumulable`)}
            sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
          <Tooltip title={t(`${OFFERS_KEY}.cumulableHelp`)} arrow>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        {showMaxEdit && (
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <Tooltip title="0 = édition illimitée">
                <TextField
                  fullWidth size="small" type="number" label="Nombre maximum d'editions"
                  value={offer.nbMaxEdit}
                  onChange={e => onChange({ nbMaxEdit: e.target.value })}
                  {...fieldError('nbMaxEdit')}
                />
              </Tooltip>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default OfferGeneralFields;
